import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { MARK_FEATURE_DISCOVERED, RECORD_FEATURE_CLICK } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Clicks on the highlighted tab after which it reverts to normal nav. */
const DISMISS_AFTER_CLICKS = 3;

interface FeatureHighlight {
  /** Whether the tab should currently render in its highlighted style. */
  highlighted: boolean;
  /** Call on every click of the highlighted tab to count toward dismissal. */
  registerClick: () => void;
}

/**
 * Per-user "discovery highlight" for a feature tab (e.g. OptiDev).
 *
 * The highlight is a personal nudge, scoped to the signed-in user via
 * `me.featureDiscovery[key] = { firstSeenAt, clicks }`:
 *  - It turns on the first time the user sees the tab (`active` true) and stays
 *    on for `durationDays` measured from `firstSeenAt`.
 *  - It turns off early once the user has clicked the tab `DISMISS_AFTER_CLICKS`
 *    times — they clearly know about the feature, so stop nagging them.
 *
 * Persistence is server-backed (so it survives across devices/sessions) and
 * fire-and-forget: `markFeatureDiscovered` stamps `firstSeenAt` once, and each
 * click fires `recordFeatureClick`. The server values land on the next `me`
 * refetch (window focus / WS); local state covers the current session so the
 * highlight appears immediately and dismisses immediately.
 *
 * @param key feature slug, must be allowlisted on the backend (e.g. 'optidev')
 * @param active whether the tab is eligible to be highlighted at all
 *   (i.e. it is currently shown to this user)
 */
export function useFeatureHighlight(
  key: string,
  active: boolean,
  durationDays = 21,
): FeatureHighlight {
  const entry = useAuthStore((s) => s.me?.featureDiscovery?.[key]);
  const [markDiscovered] = useMutation(MARK_FEATURE_DISCOVERED);
  const [recordClick] = useMutation(RECORD_FEATURE_CLICK);

  // Local click counter for this session. Seeded once from the server value the
  // first time it's known, then only bumped locally on click — we never re-add
  // the server value, so a `me` refetch can't double-count. `null` = not seeded.
  const [sessionClicks, setSessionClicks] = useState<number | null>(null);
  const serverClicks = entry?.clicks ?? null;
  if (sessionClicks === null && serverClicks !== null) {
    // setState-during-render: the React-sanctioned way to derive state from
    // newly-arrived props/store data without a sync useEffect.
    setSessionClicks(serverClicks);
  }

  // Fire-once: stamp first-seen on the server when the highlight first appears.
  const stampedRef = useRef(false);
  useEffect(() => {
    if (!active || stampedRef.current || entry?.firstSeenAt) return;
    stampedRef.current = true;
    markDiscovered({ variables: { key } }).catch(() => {});
  }, [active, key, entry?.firstSeenAt, markDiscovered]);

  // Stable window-start for this session until the server's firstSeenAt lands.
  const sessionStartRef = useRef<number | null>(null);

  const registerClick = useCallback(() => {
    setSessionClicks((prev) => (prev ?? serverClicks ?? 0) + 1);
    recordClick({ variables: { key } }).catch(() => {});
  }, [key, recordClick, serverClicks]);

  let highlighted = false;
  if (active) {
    const clicks = sessionClicks ?? serverClicks ?? 0;
    if (clicks < DISMISS_AFTER_CLICKS) {
      let shownAtMs: number | null = null;
      if (entry?.firstSeenAt) {
        shownAtMs = new Date(entry.firstSeenAt).getTime();
      } else {
        sessionStartRef.current ??= Date.now();
        shownAtMs = sessionStartRef.current;
      }
      highlighted = Date.now() - shownAtMs < durationDays * MS_PER_DAY;
    }
  }

  return { highlighted, registerClick };
}
